
import { mapGetters } from "vuex";
import { defineComponent } from "vue";
import { Rank } from "@/store/Ranks/RanksInterfaces";
import { UnilevelMember } from "@/store/Unilevel/UnilevelInterfaces";
import {
  ACADEMY_LICENSE_IDS,
  ACADEMY_SUBSCRIPTION_IDS,
  LITE_LICENSE_IDS,
  LITE_SUBSCRIPTION_IDS,
  maxRemainingDaysOf,
  normalizePackageLabel,
  PKG_FLY,
  PKG_GO,
  PKG_WIN,
} from "functions/src/Subscriptions/packageIds";
import { visibleNextRankId } from "@shared/entities/Ranks/RankVisibility";
import PackageIcon from "../common/PackageIcon.vue";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default defineComponent({
  name: "carrer-summary",
  components: {
    PackageIcon,
  },
  props: {
    userData: { required: true, type: Object as () => UnilevelMember },
    displayFooter: { required: false, type: Boolean, default: true },
  },
  computed: {
    ...mapGetters("ranks", ["rankName", "rankIconClass", "rankById"]),
    ...mapGetters("auth", ["nextRank", "currentRank"]),
    userRank(): Rank {
      return this.rankById(this.userData.currentRank);
    },
    // Esta tabla es la que de verdad PINTA el nombre del siguiente rango, y ese
    // id lo calcula el backend por `priority`. Dos casos no se enseñan:
    //  - un COFUNDADOR (2000) apunta al centinela UNKNOWN (999999), que ni se
    //    otorga ni tiene requisitos, y cuyo nombre visible es además una errata
    //    ("Unkown Secret");
    //  - un BLACK-CROWN (1300) apunta a COFUNDADOR, que solo se le enseña a
    //    quien ya lo tiene: para los demás la escalera se corta aquí.
    // La escalera es la de `userData` (puede ser otro socio), así que el rango
    // que cuenta como "el que tiene" es `userData.currentRank`.
    // Ver @shared/entities/Ranks/RankVisibility.
    userNextRankId(): string | undefined {
      return visibleNextRankId(this.userData.nextRank, {
        heldRankId: this.userData.currentRank,
        rankById: this.rankById,
      });
    },
    userNextRank(): Rank {
      // `rankById("")` devuelve undefined, igual que ya hacía con un id que no
      // resolvía; el tipo declarado se deja como estaba para no arrastrar el
      // cambio a los computeds de requisitos que cuelgan de aquí.
      return this.rankById(this.userNextRankId ?? "");
    },
    invoicedTotalString(): string {
      return this.userData.invoicedTotal.toLocaleString(undefined, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    },
    nextRankPoints(): string {
      if (this.userNextRank?.requiredPoints) {
        return this.userNextRank?.requiredPoints.toLocaleString();
      } else {
        return "0";
      }
    },
    nextRankEarning(): string {
      if (this.userNextRank?.requiredEarning) {
        return this.userNextRank?.requiredEarning.toLocaleString(undefined, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        });
      } else {
        return "0.00";
      }
    },
    totalValidPoints(): number {
      return this.userData.points.valid.left + this.userData.points.valid.right;
    },
    maxTeamPoints(): string {
      return (this.userNextRank?.maxPointsPerTeam * 1).toLocaleString();
    },
    maxAffiliatePoints(): string {
      return (this.userNextRank?.maxPointsPerAffiliate * 1).toLocaleString();
    },
    maxTeamPercentage(): number {
      return (
        (this.userNextRank?.maxPointsPerTeam * 100) /
        (this.userNextRank?.requiredPoints * 1)
      );
    },
    maxAffiliatePercentage(): number {
      return (
        (this.userNextRank?.maxPointsPerAffiliate * 100) /
        (this.userNextRank?.requiredPoints * 1)
      );
    },
    // Las cuatro leen una FAMILIA entera y se quedan con la que más días deje.
    // `undefined` (nunca tuvo ninguna de la familia) SE CONSERVA a propósito: la
    // plantilla lo distingue de un 0 (`v-if="…DaysLeft == undefined"`), así que
    // un `?? 0` aquí cambiaría lo que ve el usuario.
    academyDaysLeft(): number | undefined {
      // `trading-academy` ∪ `gtr-fly`: un traducido tiene el viejo vencido y el
      // nuevo al día, y leyendo sólo el viejo esta ficha lo pinta caducado.
      return maxRemainingDaysOf(
        this.userData.subscriptions,
        ACADEMY_SUBSCRIPTION_IDS
      );
    },
    distributorDaysLeft(): number | undefined {
      return maxRemainingDaysOf(
        this.userData.subscriptions,
        ACADEMY_LICENSE_IDS
      );
    },
    gtrLiteDaysLeft(): number | undefined {
      return maxRemainingDaysOf(
        this.userData.subscriptions,
        LITE_SUBSCRIPTION_IDS
      );
    },
    liteDistributorDaysLeft(): number | undefined {
      return maxRemainingDaysOf(this.userData.subscriptions, LITE_LICENSE_IDS);
    },
    /**
     * El nivel del miembro, traducido al vocabulario vivo. Llega de
     * `getUserPackageStatus`, pero puede venir de un dato guardado con la
     * etiqueta retirada; sin traducirlo, esta ficha se quedaría con las dos
     * mitades en blanco (ni academia ni lite) sin un solo error.
     */
    currentUserPackage(): string {
      return (
        normalizePackageLabel(
          this.userData.subscriptionPackage.package.packageId
        ) || ""
      );
    },
    isAcademyLevel(): boolean {
      return (
        this.currentUserPackage === PKG_FLY ||
        this.currentUserPackage === PKG_WIN
      );
    },
    isLiteLevel(): boolean {
      return this.currentUserPackage === PKG_GO;
    },
    hasMMIColombia(): boolean {
      const days = this.userData.subscriptions.get("mmi-colombia");
      return days ? true : false;
    },
    scholarshipEndDate(): string {
      return this.userData.scholarshipData?.periodEndDate &&
        this.userData.scholarshipData?.periodEndDate.getTime() > 0
        ? this.formatDateTimezone(
            this.userData.scholarshipData?.periodEndDate.getTime()
          )
        : "-";
    },
  },
  methods: {
    scholarshipPointsClass(points: number) {
      const classes = "fa-stack-1x fa-lg";
      if (
        this.userData.scholarshipData &&
        this.userData.scholarshipData.periodPoints >= points
      ) {
        return "fas fa-user text-orange " + classes;
      } else {
        return "far fa-user text-light-gray " + classes;
      }
    },
    scholarshipPartialPercentageStyle(points: number) {
      return {
        height: this.scholarshipPartialPercentage(points) + "%",
        overflow: "hidden",
      };
    },
    scholarshipPartialPercentage(points: number) {
      const periodPoints =
        this.userData.scholarshipData?.periodPoints || points;
      if (periodPoints >= points) return 100;
      return (periodPoints - points) * -1;
    },
    daysLeftClass(daysLeft: number): string {
      if (daysLeft <= 5) {
        return "bg-danger";
      } else if (daysLeft <= 10) {
        return "bg-warning";
      } else {
        return "bg-success";
      }
    },
    formatDateTimezone(milliseconds: number) {
      return dayjs(milliseconds).format("YYYY/MM/DD HH:mm:ss z");
    },
  },
});
